/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type TriggerProps } from '@atlaskit/popup/types';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { PromptAgentAvatar } from '../../../common/ui/prompt-agent-avatar';
import mergeRefs from '../../../common/utils/merge-refs';
import messages from './messages';

const LazyAgentMenu = lazy(async () => ({
	default: (
		await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-agent-menu/agent-menu" */ '@atlassian/conversation-assistant-agent-menu'
		)
	).AgentMenu,
}));

const BrowseAgentsModal = lazy(async () => ({
	default: (
		await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/browse-agents-modal" */ '@atlassian/conversation-assistant-agent/ui/BrowseAgentsSidebarModal'
		)
	).BrowseAgentsModal,
}));

const styles = cssMap({
	trigger: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		gap: token('space.050'),
		// Separates the trigger from the placeholder / prompt text.
		marginInlineEnd: token('space.075'),
		/*
		 * Optical alignment against the prompt's left edge. The trigger's own
		 * inline padding exists to give the hover background some breathing room,
		 * but it shouldn't compound the container's padding and push the icon
		 * inwards, so it is pulled back by the same amount.
		 */
		marginInlineStart: token('space.negative.050'),
		borderRadius: token('radius.large'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},

	icon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},

	expandingRegion: {
		display: 'grid',
		transitionProperty: 'grid-template-columns, opacity',
		transitionDuration: '0.2s',
		transitionTimingFunction: 'ease-out',
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0s',
		},
	},

	expanded: {
		gridTemplateColumns: '1fr',
		opacity: 1,
	},

	collapsed: {
		gridTemplateColumns: '0fr',
		opacity: 0,
	},

	// `overflow: hidden` + `min-width: 0` let the content be clipped as the grid
	// column collapses to `0fr`.
	expandingContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		overflow: 'hidden',
		minWidth: '0px',
		whiteSpace: 'nowrap',
		font: token('font.body'),
	},

	staticChevron: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
});

type BrowseAgentsHostProps = {
	isOpen: boolean;
	siteId: string;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	onClose: () => void;
	onAgentSelect: (agent: Agent) => void;
};

const BrowseAgentsHost = ({
	isOpen,
	siteId,
	assistanceServiceParams,
	onClose,
	onAgentSelect,
}: BrowseAgentsHostProps): React.JSX.Element | null => {
	// Studio browse is opened by `AgentMenu`'s `ModalTrigger`. This host is
	// only the legacy modal. Keep `isOpen` true in that case so the prompt
	// still treats the Studio modal as in-flow (not an outside click).
	if (
		(fg('rovo_agents_studio_browse_kit_chat') &&
			expVal('jira_browse_agent_modal_update', 'isEnabled', false)) ||
		!isOpen
	) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<BrowseAgentsModal
				siteId={siteId}
				onClose={onClose}
				onClickChat={onAgentSelect}
				handleClickConversationStarter={onAgentSelect}
				assistanceServiceParams={assistanceServiceParams}
			/>
		</Suspense>
	);
};

export type AgentSelectorProps = {
	/**
	 * The currently selected agent, or `null`/`undefined` when the default Rovo
	 * assistant is handling the prompt.
	 */
	agent?: Agent | null;
	/** `null` clears the selection and returns the prompt to default Rovo. */
	onAgentSelect: (agent: Agent | null) => void;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	siteId: string;
	isDisabled?: boolean;
	/**
	 * Mirrors the prompt's `compact` mode (comment / chromeless hosts). Keeps the
	 * trigger icon-only so expanding the agent name cannot crush the prompt
	 * (CCI-19349).
	 */
	compact?: boolean;
	/**
	 * When false, the menu renders into the trigger's DOM tree. Defaults to
	 * portalling so the menu can paint over editor and product chrome instead of
	 * being clipped by the page.
	 *
	 * The inline prompt dismisses on outside interaction; portalled menu clicks
	 * are treated as inside via `data-chat-header-menu` on the shared popup.
	 */
	shouldRenderInPortal?: boolean;
	/**
	 * Shared with the `+` action-button browse flow so a browse modal opened
	 * from this picker is not treated as an outside click that dismisses the prompt.
	 */
	isBrowseAgentsModalOpen?: boolean;
	setIsBrowseAgentsModalOpen?: (isBrowseAgentsModalOpen: boolean) => void;
	/** Hides the agent name in narrow embeddings. */
	hideAgentName?: boolean;
};

type AgentSelectorMenuLayout = {
	maxHeight?: number;
	placement: 'top-start' | 'bottom-start';
};

/** Keep the menu off the viewport edge. `token('space.100')` is a CSS string, not a number. */
const VIEWPORT_GAP = 8;

const getAgentSelectorMenuLayout = (
	trigger: HTMLElement,
	menu: HTMLElement | null,
): AgentSelectorMenuLayout => {
	const triggerRect = trigger.getBoundingClientRect();
	const spaceAbove = Math.max(0, triggerRect.top - VIEWPORT_GAP);
	const spaceBelow = Math.max(0, window.innerHeight - triggerRect.bottom - VIEWPORT_GAP);
	// Prefer scrollHeight so a popup that is still filling in (or clipped) is
	// not treated as short enough to open unconstrained.
	const menuHeight = menu
		? menu.scrollHeight || menu.offsetHeight || menu.getBoundingClientRect().height
		: 0;
	if (menuHeight <= 0) {
		return { placement: 'bottom-start' };
	}
	if (spaceAbove >= menuHeight) {
		return { placement: 'top-start' };
	}
	if (spaceBelow >= menuHeight) {
		return { placement: 'bottom-start' };
	}
	const placement = spaceAbove > spaceBelow ? 'top-start' : 'bottom-start';
	const available = Math.max(spaceAbove, spaceBelow);
	return available > 0 ? { placement, maxHeight: available } : { placement };
};

/**
 * Agent selector rendered to the LEFT of the inline prompt input.
 *
 * At rest it shows only the current agent's avatar (or the generic agent icon
 * for default Rovo). On hover / keyboard focus — and while the menu is open —
 * it expands to reveal the agent name and a chevron. Clicking opens the shared
 * `AgentMenu`, which supplies the "Switch to another agent" heading, agent
 * search, the agent list, and the Browse / Create agent actions.
 */
export const AgentSelector = ({
	agent,
	onAgentSelect,
	assistanceServiceParams,
	siteId,
	isDisabled,
	compact,
	shouldRenderInPortal = true,
	isBrowseAgentsModalOpen = false,
	setIsBrowseAgentsModalOpen,
	hideAgentName,
}: AgentSelectorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [isPointerOver, setIsPointerOver] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [localBrowseAgentsModalOpen, setLocalBrowseAgentsModalOpen] = useState(false);
	// `AgentMenu` pulls in Relay and the agent permission controllers, so it is
	// only mounted once the user actually opens the menu. Until then the trigger
	// renders on its own and the prompt pays nothing for it.
	const [hasOpened, setHasOpened] = useState(false);
	const isBrowseOpen = setIsBrowseAgentsModalOpen
		? isBrowseAgentsModalOpen
		: localBrowseAgentsModalOpen;
	const setBrowseOpen = setIsBrowseAgentsModalOpen ?? setLocalBrowseAgentsModalOpen;
	const [layout, setLayout] = useState<AgentSelectorMenuLayout>({ placement: 'bottom-start' });
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const menuObserverRef = useRef<ResizeObserver | null>(null);

	const updatePlacement = useCallback(() => {
		const trigger = triggerRef.current;
		if (!trigger) {
			return;
		}
		const next = getAgentSelectorMenuLayout(trigger, menuRef.current);
		setLayout((current) =>
			current.placement === next.placement && current.maxHeight === next.maxHeight ? current : next,
		);
	}, []);

	const setMenuRef = useCallback(
		(node: HTMLDivElement | null) => {
			menuObserverRef.current?.disconnect();
			menuObserverRef.current = null;
			menuRef.current = node;
			if (!node) {
				return;
			}
			updatePlacement();
			if (typeof ResizeObserver === 'undefined') {
				return;
			}
			const observer = new ResizeObserver(() => {
				updatePlacement();
			});
			observer.observe(node);
			menuObserverRef.current = observer;
		},
		[updatePlacement],
	);

	useEffect(
		() => () => {
			menuObserverRef.current?.disconnect();
		},
		[],
	);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		updatePlacement();
		// Capture so editor / page scroll (not just window) updates placement.
		window.addEventListener('scroll', updatePlacement, true);
		window.addEventListener('resize', updatePlacement);
		return () => {
			window.removeEventListener('scroll', updatePlacement, true);
			window.removeEventListener('resize', updatePlacement);
		};
	}, [isOpen, updatePlacement]);

	// Keep the label expanded while the menu is open so the trigger doesn't
	// collapse out from under the popup when the pointer moves into it.
	// Compact (comment) hosts stay icon-only — there is no spare width for the
	// name, and the trigger's aria-label already announces the current agent.
	const isExpanded = !compact && (isOpen || isPointerOver || isFocused);

	const agentName = agent?.name ?? formatMessage(messages.defaultAgentName);

	/**
	 * Only keyboard focus should expand the trigger. Closing the menu returns
	 * focus here, so treating every focus as an expand left the label stuck open
	 * after picking an agent with the mouse.
	 */
	const handleFocus = useCallback((event: React.FocusEvent<HTMLButtonElement>) => {
		try {
			setIsFocused(event.currentTarget.matches(':focus-visible'));
		} catch {
			// `:focus-visible` is unsupported in some test environments.
			setIsFocused(false);
		}
	}, []);

	const handleAgentSelect = useCallback(
		(selectedAgent: Agent | undefined) => {
			// `AgentMenu` passes `undefined` for its "Chat with Rovo" item, which is
			// how the user gets back to the default assistant. Coerced to `null` so
			// the controller clears the selection rather than ignoring it.
			onAgentSelect(selectedAgent ?? null);
			setIsOpen(false);
		},
		[onAgentSelect],
	);

	const handleBrowseAgentClick = useCallback(() => {
		setIsOpen(false);
		setBrowseOpen(true);
	}, [setBrowseOpen]);

	const handleBrowseModalClose = useCallback(() => {
		setBrowseOpen(false);
	}, [setBrowseOpen]);

	const handleBrowseModalAgentSelect = useCallback(
		(selectedAgent: Agent) => {
			onAgentSelect(selectedAgent);
			setBrowseOpen(false);
			setIsOpen(false);
		},
		[onAgentSelect, setBrowseOpen],
	);

	const renderTrigger = useCallback(
		(triggerProps?: TriggerProps) => (
			<Pressable
				{...triggerProps}
				ref={triggerProps?.ref ? mergeRefs([triggerRef, triggerProps.ref]) : triggerRef}
				xcss={styles.trigger}
				testId="inline-chat-prompt-agent-selector"
				isDisabled={isDisabled}
				aria-label={formatMessage(messages.selectAgentLabel, { agentName })}
				aria-expanded={isOpen}
				aria-haspopup="menu"
				onClick={() => {
					updatePlacement();
					setHasOpened(true);
					setIsOpen((open) => !open);
				}}
				onMouseEnter={() => setIsPointerOver(true)}
				onMouseLeave={() => setIsPointerOver(false)}
				onFocus={handleFocus}
				onBlur={() => setIsFocused(false)}
			>
				<Tooltip
					content={hideAgentName ? agentName : null}
					tag="span"
					position="top"
					testId="inline-chat-prompt-agent-icon"
				>
					<span css={styles.icon}>
						{agent ? <PromptAgentAvatar agent={agent} /> : <RovoHexIcon label="" size="xsmall" />}
					</span>
				</Tooltip>
				{hideAgentName ? (
					<span css={styles.staticChevron} aria-hidden="true">
						<ChevronDownIcon label="" size="small" color={token('color.icon.subtle')} />
					</span>
				) : (
					<span
						css={[styles.expandingRegion, isExpanded ? styles.expanded : styles.collapsed]}
						aria-hidden="true"
						data-expanded={isExpanded}
						data-testid="inline-chat-prompt-agent-selector-label"
					>
						<span css={styles.expandingContent}>
							{agentName}
							<ChevronDownIcon label="" />
						</span>
					</span>
				)}
			</Pressable>
		),
		[
			agent,
			agentName,
			formatMessage,
			handleFocus,
			hideAgentName,
			isDisabled,
			isExpanded,
			isOpen,
			updatePlacement,
		],
	);

	if (!hasOpened) {
		return renderTrigger();
	}

	return (
		// `AgentMenu` calls `useRelayEnvironment()`. The inline prompt is only
		// wrapped in a Relay provider on the agent-mentions path, so without this
		// the menu would throw for every other consumer. The conditional provider
		// reuses an ancestor environment when one already exists.
		<ConditionalRelayEnvironmentProvider>
			<Suspense fallback={renderTrigger()}>
				<LazyAgentMenu
					selectedAgent={agent ?? undefined}
					isAgentDropdownOpen={isOpen}
					setIsAgentDropdownOpen={setIsOpen}
					onClose={() => setIsOpen(false)}
					trigger={renderTrigger}
					placement={layout.placement}
					maxHeight={layout.maxHeight}
					shouldFlip={false}
					shouldRenderToParent={!shouldRenderInPortal}
					menuRef={setMenuRef}
					siteId={siteId}
					assistanceServiceParams={assistanceServiceParams}
					onAgentSelect={handleAgentSelect}
					onBrowseAgentClick={handleBrowseAgentClick}
					onBrowseAgentsModalClose={handleBrowseModalClose}
					touchpointSource="inline-chat-prompt-agent-selector"
					autofocus
					// `onAgentSelect` takes precedence over starting a new conversation,
					// which is Rovo Chat behaviour and not meaningful for the inline prompt.
					createConversationOptimistically={() => {}}
				/>
			</Suspense>
			<BrowseAgentsHost
				isOpen={isBrowseOpen}
				siteId={siteId}
				assistanceServiceParams={assistanceServiceParams}
				onClose={handleBrowseModalClose}
				onAgentSelect={handleBrowseModalAgentSelect}
			/>
		</ConditionalRelayEnvironmentProvider>
	);
};
